// Select Component

import React, { Component } from 'react'
import ReactDom from 'react-dom'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import _ from 'lodash'

import IntlMessage from '../../common/intl/intl_message.jsx'
import IntlUtil from '../../../lib/intlutil.jsx'
import { Spinner } from '../../common/spinner.jsx'

class Select extends Component {
  constructor (props) {
    super(props)

    this.state = {
      active: false,
      keyword: '',
      error: false,
      value: props.value,
      focusedIndex: -1
    }

    this.data = {
      translatedOptions: props.options
        ? this._getTranslatedOptions(props.options, props.multilingualOptions)
        : []
    }

    this._references = {
      select: React.createRef(),
      selectInput: React.createRef(),
      selectDrop: React.createRef(),
      searchinput: React.createRef(),
      dropdownContent: React.createRef()
    }
  }

  static get propTypes () {
    return {
      label: PropTypes.string,
      onChange: PropTypes.func,
      options: PropTypes.array.isRequired,
      required: PropTypes.bool,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      className: PropTypes.string,
      multilingualOptions: PropTypes.bool,
      disabled: PropTypes.bool,
      onBlur: PropTypes.func,
      onKeyPress: PropTypes.func,
      onKeyDown: PropTypes.func,
      icon: PropTypes.string,
      placeholder: PropTypes.string,
      isLoading: PropTypes.bool
    }
  }

  static get defaultProps () {
    return {
      label: '',
      onChange: null,
      options: [],
      required: false,
      value: '',
      name: '',
      className: '',
      multilingualOptions: false,
      disabled: false,
      onBlur: null,
      onKeyPress: null,
      onKeyDown: null,
      icon: null,
      placeholder: '',
      isLoading: false
    }
  }

  componentDidMount () {
    window.rootApp.addEventListener('click', this._handleDocumentClick)
    document.body.addEventListener('keydown', this._handleDocumentKeyDown)
  }

  componentWillUnmount () {
    window.rootApp.removeEventListener('click', this._handleDocumentClick)
    document.body.removeEventListener('keydown', this._handleDocumentKeyDown)
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps (nextProps) {
    this.setState({ value: nextProps.value })

    if (this.data.options !== nextProps.options) {
      this.data.translatedOptions = this._getTranslatedOptions(
        nextProps.options,
        nextProps.multilingualOptions
      )
    }
  }

  _getTranslatedOptions (options, bMultilingual) {
    const newOptions = []

    if (options && options.length > 0) {
      options.map(option => {
        const label = bMultilingual
          ? IntlUtil.convertMessage(option.label)
          : option.label

        newOptions.push({
          label: label,
          value: option.value,
          isDisabled: option.isDisabled || false
        })
      })
    }

    return newOptions
  }

  render () {
    const className = classnames(
      'select',
      {
        active: this.state.active,
        has_error: this.state.error,
        disabled: this.props.disabled
      },
      this.props.className
    )

    const selected = this._getSelectedItem()
    const iconName = `icon icon-${this.props.icon}`
    const inputClassName = `selectinput ${
      this.props.icon ? 'selectinput_alt' : ''
    }`
    const options = this.data.translatedOptions

    return (
      <div className={className} ref={this._references.select}>
        <div className='selectsingle'>
          <span className='selecticon selecticon_normal'>
            <i className='icon icon-form-selectdownarw' />
          </span>
          <span className='selecticon selecticon_active'>
            <i className='icon icon-form-selectuparw' />
          </span>
          {this.props.icon && (
            <span className='selecticon selecticon_iconcont'>
              <i className={iconName} />
            </span>
          )}
          {this.props.isLoading && (
            <span className='selectspinner'>
              <Spinner size={15} color='gray' />
            </span>
          )}
          <input
            className={inputClassName}
            onMouseDown={this._handleMouseDown.bind(this)}
            onFocus={this._handleInputFocus.bind(this)}
            value={
              selected && selected.label
                ? this.props.multilingualOptions
                  ? IntlUtil.convertMessage(selected.label)
                  : selected.label
                : ''
            }
            name={this.props.name}
            readOnly
            ref={this._references.selectInput}
            required={this.props.required ? this.props.required : false}
            onBlur={this._onBlurInput.bind(this)}
            onKeyDown={this._onKeyPressInput.bind(this)}
          />
          <span className='selectlabel'>
            <IntlMessage ident={this.props.label} />
          </span>
        </div>
        <div className='selectdrop' ref={this._references.selectDrop}>
          {options && options.length > 6 && (
            <div className='selectsearch'>
              <input
                ref={this._references.searchinput}
                autoComplete='off'
                type='text'
                name='keyword'
                value={this.state.keyword}
                onKeyDown={this._onSearchKeyUp.bind(this)}
                onChange={this._handleChange.bind(this)}
              />
              <i className='icon icon-form-select-search' />
            </div>
          )}
          <ul
            className='selectdropvalues'
            ref={this._references.dropdownContent}
          >
            {this._renderOptions()}
          </ul>
        </div>
      </div>
    )
  }

  /* Event Handlers */
  _handleDocumentClick = evt => {
    if (
      this.state.active &&
      this._references.select.current &&
      this._references.selectDrop.current
    ) {
      const area = ReactDom.findDOMNode(this._references.select.current)
      const dropArea = ReactDom.findDOMNode(this._references.selectDrop.current)

      if (!area.contains(evt.target) && !dropArea.contains(evt.target)) {
        this.setState({ active: false, focusedIndex: -1 })
      }
    }
  }

  _handleDocumentKeyDown = ev => {
    this._handleOptionFocus(ev)
  }

  _handleOptionFocus (ev) {
    let { focusedIndex, active } = this.state

    if (active) {
      const keyword = this.state.keyword.toLowerCase()
      const options = _.filter(
        this.data.translatedOptions,
        optn =>
          optn.label !== '' || optn.label.toLowerCase().indexOf(keyword) !== -1
      )

      if (options.length > 0) {
        const keyCode = ev.keyCode || 0

        if (keyCode === 40 || keyCode === 38) {
          ev.preventDefault()
          ev.stopPropagation()

          const bDown = keyCode === 40
          const incr = bDown ? 1 : -1
          focusedIndex = this._getNextFocus(options, focusedIndex, incr)

          if (this._references.dropdownContent.current) {
            let scrollTop = 0
            const contNode = ReactDom.findDOMNode(
              this._references.dropdownContent.current
            )

            if (options.length > 6 && focusedIndex >= 5) {
              const { height: childHeight } = ReactDom.findDOMNode(
                contNode.children[focusedIndex]
              ).getBoundingClientRect()

              if (bDown) {
                scrollTop = contNode.scrollTop + childHeight
              } else {
                if (contNode.scrollTop === 0) {
                  let contHeight = 0
                  options.forEach((_obj, optionIdx) => {
                    const { height: childHeight } = ReactDom.findDOMNode(
                      contNode.children[optionIdx]
                    ).getBoundingClientRect()
                    contHeight += childHeight
                  })
                  scrollTop = contHeight - childHeight
                } else {
                  scrollTop = contNode.scrollTop - childHeight
                }
              }
            }

            contNode.scrollTop = scrollTop
          }

          this.setState({ focusedIndex })
        } else if (keyCode === 13) {
          ev.preventDefault()
          ev.stopPropagation()

          if (focusedIndex !== -1 && options[focusedIndex]) {
            this._handleSelect(options[focusedIndex].value, ev)
          }
        }
      }
    }
  }

  _getNextFocus (options, focusedIndex, incr) {
    const totalOptions = options.length
    const idx = 0

    // eslint-disable-next-line no-unmodified-loop-condition
    while (idx < totalOptions) {
      focusedIndex += incr

      if (focusedIndex < 0) {
        focusedIndex = totalOptions - 1
      } else if (focusedIndex === totalOptions) {
        focusedIndex = 0
      }

      if (options[focusedIndex]) {
        return focusedIndex
      }
    }

    return -1
  }

  _handleChange (ev) {
    this.setState({ keyword: ev.target.value })
  }

  _handleSelect (value, ev) {
    ev.preventDefault()

    this.setState({
      value: value,
      active: false,
      error: false,
      focusedIndex: -1
    })

    if (this.props.onChange) {
      this.props.onChange(value, ev)
    }
  }

  _handleMouseDown (ev) {
    ev.preventDefault()

    setTimeout(() => {
      if (this._references.selectInput.current) {
        if (document.activeElement === this._references.selectInput.current) {
          this._references.selectInput.current.blur()
        } else {
          this._references.selectInput.current.focus()
          this._references.selectInput.current.blur()
        }
      }
    })
  }

  _handleInputFocus () {
    this._toggleDropdown()
  }

  _onSearchKeyUp (ev) {
    if (ev.keyCode === 9 || ev.keyCode === 27) {
      this.setState({ active: false, focusedIndex: -1 })

      ev.preventDefault()
      ev.stopPropagation()
    }
  }

  _onBlurInput (ev) {
    if (this.props.onBlur) {
      this.props.onBlur(ev)
    }
  }

  _onKeyPressInput (ev) {
    if (ev.keyCode === 13 || ev.keyCode === 9 || ev.keyCode === 27) {
      this._toggleDropdown()
      if (this.props.onKeyPress) {
        this.props.onKeyPress(ev)
      }
    }
  }

  componentDidUpdate () {
    if (this.state.active && this._references.searchinput.current) {
      this._references.searchinput.current.focus()
    }
  }

  /* Private Functions */
  _toggleDropdown () {
    this.setState({ active: !this.state.active })
  }

  _renderOptions () {
    const keyword = this.state.keyword.toLowerCase()
    const options = _.filter(
      this.data.translatedOptions,
      optn =>
        optn.label !== '' || optn.label.toLowerCase().indexOf(keyword) !== -1
    )

    const { focusedIndex } = this.state

    if (options.length > 0) {
      return options.map((option, idx) => {
        let className =
          option.value === this.props.value ? 'selectdropselected' : ''
        if (option.isDisabled) {
          className += ' disabled'
        } else if (focusedIndex === idx) {
          className += ' focusitem'
        }

        if (
          option.label &&
          option.label
            .toLowerCase()
            .indexOf(this.state.keyword.toLowerCase()) !== -1
        ) {
          return (
            <li
              key={idx}
              className={className}
              onClick={this._handleSelect.bind(this, option.value)}
            >
              {option.label}
            </li>
          )
        }
        return ''
      })
    }

    return (
      <li className='selectmsg'>
        <IntlMessage ident='common.list.noSelectOptions' /> {this.state.keyword}
      </li>
    )
  }

  validate () {
    const selected = this._getSelectedItem()
    if (!selected.label) {
      this.setState({ error: true })

      return false
    }

    return true
  }

  _getSelectedItem () {
    const selVal = this.props.value
    for (const idx in this.props.options) {
      if (this.props.options[idx]) {
        const item = this.props.options[idx]
        if (item.value === selVal) {
          return item
        }
      }
    }

    return ''
  }

  clear () {
    this.setState({
      error: false,
      value: ''
    })
  }

  clearError () {
    this.setState({
      error: false
    })

    return this.state.error
  }
}

export default Select
